import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import SnowflakeIcon from 'ext-ui/svg/icon/SnowflakeIcon'
import React, { ReactElement } from 'react'
import type {
  PhysicalCardLifecycleAction,
  PhysicalCardLifecycleControls,
} from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import { useTranslate } from 'src/localization/Trns'
import type { PhysicalCardManagementStatus } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

import SettingsTile from '../SettingsTile/SettingsTile'
import style from './CardStatusLifecycleTile.style'

type CardStatusLifecycleTileProps = {
  /** Grid-column class from the bento parent. */
  className?: string
  status: PhysicalCardManagementStatus | undefined
  lifecycleControls: PhysicalCardLifecycleControls
}

type LifecycleRowProps = {
  action: PhysicalCardLifecycleAction
  title: string
  description: string
  ctaLabel: string
  testId: string
  lifecycleControls: PhysicalCardLifecycleControls
}

function LifecycleRow({
  action,
  title,
  description,
  ctaLabel,
  testId,
  lifecycleControls,
}: LifecycleRowProps): ReactElement {
  return (
    <div className="CardStatusLifecycleTile-row">
      <div className="CardStatusLifecycleTile-rowText">
        <div className="CardStatusLifecycleTile-rowTitle">{title}</div>
        <div className="CardStatusLifecycleTile-rowSub">{description}</div>
      </div>
      <Button
        variant="secondary"
        dropShadow={false}
        disabled={lifecycleControls.isActionInProgress(action)}
        onClick={() => lifecycleControls.openAction(action, 'cardSettings')}
        data-testid={`${testId}-cta`}
      >
        {ctaLabel}
      </Button>
    </div>
  )
}

const isFreezeRowVisible = (
  status: PhysicalCardManagementStatus | undefined,
  lifecycleControls: PhysicalCardLifecycleControls,
): boolean =>
  (status === 'ACTIVE' && lifecycleControls.availability.freeze) ||
  (status === 'FROZEN' && lifecycleControls.availability.unfreeze)

/**
 * Whether this tile renders anything at all — false on a card where every
 * lifecycle action is denied (e.g. a processor that reports
 * PROCESSOR_UNSUPPORTED across the board).
 *
 * Exported because the bento parent has to know BEFORE it renders: the tiles
 * are pinned to grid columns, so the parent orders the DOM to match the
 * resulting visual order, and it cannot do that if the tile decides to vanish
 * on its own. The component keeps its own guard so the two can never disagree.
 */
export const isCardStatusLifecycleTileVisible = (
  status: PhysicalCardManagementStatus | undefined,
  lifecycleControls: PhysicalCardLifecycleControls,
): boolean =>
  isFreezeRowVisible(status, lifecycleControls) ||
  lifecycleControls.availability.lostOrStolen ||
  lifecycleControls.availability.reportFraud ||
  lifecycleControls.availability.replace ||
  lifecycleControls.availability.cancel

export default function CardStatusLifecycleTile({
  className,
  status,
  lifecycleControls,
}: CardStatusLifecycleTileProps): ReactElement | null {
  const translate = useTranslate()
  const isFrozen = status === 'FROZEN'
  const showFreeze = isFreezeRowVisible(status, lifecycleControls)

  if (!isCardStatusLifecycleTileVisible(status, lifecycleControls)) return null

  return (
    <SettingsTile
      className={className}
      title={translate('Card status & lifecycle')}
    >
      <div className="CardStatusLifecycleTile" css={style}>
        {showFreeze && (
          <div className="CardStatusLifecycleTile-freezeRow">
            <div className="CardStatusLifecycleTile-rowText">
              <div className="CardStatusLifecycleTile-freezeHeading">
                <span className="CardStatusLifecycleTile-rowTitle">
                  {isFrozen ? translate('Unfreeze') : translate('Freeze')}
                </span>
                <Pill size="small" color={isFrozen ? 'pending' : 'highlight'}>
                  {isFrozen ? translate('Frozen') : translate('Active')}
                </Pill>
              </div>
              <div className="CardStatusLifecycleTile-rowSub">
                {translate(
                  'Freezing blocks new authorizations while credits, refunds, disputes, and statements keep processing.',
                )}
              </div>
            </div>
            <Button
              variant="secondary"
              dropShadow={false}
              iconLeft={<SnowflakeIcon />}
              onClick={() =>
                void lifecycleControls.toggleFreeze('cardSettings')
              }
              disabled={
                !lifecycleControls.canToggleFreeze ||
                lifecycleControls.isFreezePending
              }
              loading={lifecycleControls.isFreezePending}
              aria-busy={lifecycleControls.isFreezePending}
              data-testid="freeze-cta"
            >
              {isFrozen ? translate('Unfreeze') : translate('Freeze')}
            </Button>
          </div>
        )}

        {lifecycleControls.availability.lostOrStolen && (
          <LifecycleRow
            action="lostOrStolen"
            testId="report-lost"
            title={translate('Report lost or stolen')}
            description={translate(
              'Block the card and ship a replacement with a new number.',
            )}
            ctaLabel={translate('Report')}
            lifecycleControls={lifecycleControls}
          />
        )}
        {lifecycleControls.availability.reportFraud && (
          <LifecycleRow
            action="reportFraud"
            testId="report-fraud"
            title={translate('Report fraud')}
            description={translate(
              "Flag fraudulent activity for the issuer's fraud team to review.",
            )}
            ctaLabel={translate('Report')}
            lifecycleControls={lifecycleControls}
          />
        )}
        {lifecycleControls.availability.replace && (
          <LifecycleRow
            action="replace"
            testId="replace-card"
            title={translate('Replace card')}
            description={translate(
              'Order a new physical card and keep the same number.',
            )}
            ctaLabel={translate('Replace')}
            lifecycleControls={lifecycleControls}
          />
        )}
        {lifecycleControls.availability.cancel && (
          <LifecycleRow
            action="cancel"
            testId="cancel-card"
            title={translate('Cancel card')}
            description={translate(
              'Permanently close this physical card and its virtual cards.',
            )}
            ctaLabel={translate('Cancel card')}
            lifecycleControls={lifecycleControls}
          />
        )}
      </div>
    </SettingsTile>
  )
}
